import React, { ReactNode, useEffect, useState } from 'react';
import { MealContext } from './context';
import { MealCategoryType, MealType, CreateMealType, BrandOption, BrandType } from 'utils/types';
import { useLocation, useParams } from 'react-router-dom';
import { ToastMessage } from '@cokitchen/cokitchen-components';
import { toast } from 'react-toastify';
import { apiService } from 'services/base.service';
import { MultiselectItem } from '@cokitchen/cokitchen-components/dist/form/multiselect/types';
import { MetadataItemType } from 'pages/metadata/types';
import { isEditMeal } from 'utils/functions';
import useDashboardContext from 'contexts/dashboard.context';

const MealProvider = ({ children }: { children: ReactNode }) => {
  const { id } = useParams();
  const { activeCokitchen, mealInfo, category } = useDashboardContext();
  const [brandLoading, setBrandLoading] = useState(false);
  const [brandsList, setBrandsList] = useState<MultiselectItem[]>([]);
  const [categoryList, setCategoryList] = useState<MultiselectItem[]>([]);
  const [baseMealList, setBaseMealList] = useState<MultiselectItem[]>([]);
  const [allBrands, setAllBrands] = useState<BrandType[]>([]);

  const [selectedKeywords, setSelectedKeywords] = useState<MetadataItemType[]>([]);
  const [selectedDescriptiveMetadata, setSelectedDescriptiveMetadata] = useState<
    MetadataItemType[]
  >([]);
  const [selectedBusinessMetadata, setSelectedBusinessMetadata] = useState<MetadataItemType[]>([]);

  const [formData, setFormData] = useState<CreateMealType>({} as CreateMealType);
  const [loading, setLoading] = useState(false);
  const [meal, setMeal] = useState({} as MealType);
  const [initialForm, setInitialForm] = useState({} as CreateMealType);
  const [selectedBrand, setSelectedBrand] = useState<BrandOption[]>([]);
  const [newCategory, setNewCategory] = useState(false);
  const [selectedCategoryList, setSelectedCategoryList] = useState<MultiselectItem[]>([]);
  const [selectedBaseMeal, setSelectedBaseMeal] = useState<MultiselectItem[]>([]);
  const [mealImage, setMealImage] = useState<File[] | null>(null);
  const [disabled, setDisabled] = useState(false);
  const [uploading, setUploading] = useState(false);

  const { pathname } = useLocation();

  const MAX_UPLOAD = 6;

  const getDefaultImages = (images: string[]) => {
    const imagesUploaded = [...images];
    const imagesUploadedCount = imagesUploaded.length;
    const imagesNotUploadedCount = MAX_UPLOAD - imagesUploadedCount;
    const imagesNotUploaded = Array(imagesNotUploadedCount).fill('');
    return imagesUploaded.concat(imagesNotUploaded);
  };

  const getMealById = async () => {
    if (!isEditMeal(pathname)) return;
    try {
      setLoading(true);
      const res = await apiService.get(`meal/${id}`);
      if (res?.data) {
        setMeal(res?.data?.data);
      }
    } catch (err: any) {
      toast(<ToastMessage type='error' message={err?.response?.data?.message} />);
    } finally {
      setLoading(false);
    }
  };

  const formatMetadaList = () => {
    const keywords = meal?.meal_keywords?.map((k) => ({ name: k.name, id: k.id }));
    const descriptive = meal?.meal_descriptive_metadatas?.map((k) => ({ name: k.name, id: k.id }));
    const business = meal?.meal_business_metadatas?.map((k) => ({ name: k.name, id: k.id }));
    setSelectedKeywords(keywords || []);
    setSelectedDescriptiveMetadata(descriptive || []);
    setSelectedBusinessMetadata(business || []);
  };

  const formatFormData = () => {
    formatMetadaList();
    const data: CreateMealType = {
      base_meal_id: [meal?.base_meal_id],
      brand_id: [meal?.brand_id],
      meal_category_id: [meal?.meal_category_id],
      name: meal?.name,
      slug: meal?.slug,
      description: meal?.description ?? '',
      amount: meal?.amount,
      calories: meal?.calories ? String(meal?.calories) : '',
      preparation_time: String(meal?.preparation_time),
      images: meal?.images ?? [],
      meal_keywords: meal?.meal_keywords,
      meal_descriptive_metadatas: meal?.meal_descriptive_metadatas,
      meal_business_metadatas: meal?.meal_business_metadatas,
      new: meal?.new ?? true,
      active: meal?.active ?? true,
      minimum_age: meal.minimum_age ?? '0',
      available_no: String(meal?.available_no) ?? '',
      is_addon: meal?.is_addon ?? false,
      alcohol: meal?.alcohol ?? false,
      budget_combo: meal?.budget_combo ?? false
    };
    setInitialForm(
      isEditMeal(pathname) ? { ...data, images: getDefaultImages(data.images) } : data
    );
    setFormData(isEditMeal(pathname) ? { ...data, images: getDefaultImages(data.images) } : data);
  };

  const fetchBrands = async () => {
    if (!category) return;
    try {
      setBrandLoading(true);
      const params = {
        cokitchen_id: activeCokitchen.id
      };
      const res = await apiService.get('brand_management/brands', {
        params
      });
      const data: BrandType[] = res.data?.filter(
        (b: BrandType) => b.item_type === category.toUpperCase() && !b.archived
      );

      const brandArray = data.map((brand) => {
        return {
          value: brand.id,
          text: brand.name
        };
      });
      setBrandsList(brandArray);
    } finally {
      setBrandLoading(false);
    }
  };
  const fetchCategory = async () => {
    if (!selectedBrand?.length) return;
    try {
      const res = await apiService.post(`marketing/brand/meal-categories`, {
        data: { brand_ids: selectedBrand.map((brand) => brand.value) }
      });
      const { data } = res.data;
      const categoryArray = data.map((category: MealCategoryType) => {
        return {
          value: category.id,
          text: isEditMeal(pathname) ? category.name : `${category?.brand.name} - ${category.name}`
        };
      });
      setSelectedCategoryList([]);
      setCategoryList(categoryArray);
    } finally {
      setBrandLoading(false);
    }
  };
  const fetchBaseMeals = async () => {
    try {
      const res = await apiService.get(`base/meals/names`, {});
      const { data } = res.data;
      const baseMealsArray = data.map((baseMeal: MealType) => {
        return {
          value: baseMeal.id,
          text: baseMeal.name
        };
      });
      setSelectedBaseMeal([]);
      setBaseMealList(baseMealsArray);
    } finally {
      setBrandLoading(false);
    }
  };

  const formatSelectedCategory = () => {
    if (!brandsList) return;
    const category = categoryList?.find((item) => item.value === meal?.meal_category_id);
    if (category) setSelectedCategoryList([category]);
  };

  const formatSelectedBaseMeal = () => {
    const baseMeal = baseMealList?.find((item) => item.value === meal?.base_meal_id);
    if (baseMeal) setSelectedBaseMeal([baseMeal]);
  };
  useEffect(() => {
    if (mealInfo && mealInfo.length > 0 && brandsList && brandsList.length > 0) {
      setSelectedBrand(
        mealInfo?.map((brand) => {
          return {
            value: brand?.brand_id,
            text: brandsList.find((item) => item.value === brand?.brand_id)?.text || ''
          };
        })
      );
    }
  }, [mealInfo, brandsList]);
  useEffect(() => {
    if (mealInfo && mealInfo.length > 0 && categoryList && categoryList.length > 0) {
      const updatedSelectedCategoryList = [
        ...mealInfo.map((category) => {
          return {
            value: category?.meal_category_id,
            text: categoryList.find((item) => item.value === category?.meal_category_id)?.text || ''
          };
        }),
        ...selectedCategoryList.map((category) => {
          return {
            value: category?.value,
            text: categoryList.find((item) => item.value === category?.value)?.text || ''
          };
        })
      ];

      const hasDuplicateValue = (arr: any[], value: string) => {
        return arr.some((item) => item.value === value);
      };

      const uniqueSelectedCategoryList = updatedSelectedCategoryList.filter(
        (category, index, self) => {
          return !hasDuplicateValue(self.slice(0, index), category.value);
        }
      );

      setSelectedCategoryList(uniqueSelectedCategoryList);
    }
  }, [categoryList]);

  useEffect(() => {
    if (meal?.brand && isEditMeal(pathname))
      setSelectedBrand([
        ...selectedBrand,
        {
          value: meal?.brand?.id,
          text: meal?.brand?.name
        }
      ]);
  }, [meal]);

  useEffect(() => {
    fetchBrands();
  }, [category, activeCokitchen]);

  useEffect(() => {
    getMealById();
  }, []);

  useEffect(() => {
    fetchCategory();
  }, [selectedBrand]);

  useEffect(() => {
    fetchBaseMeals();
  }, []);

  useEffect(() => {
    setNewCategory(false);
  }, []);

  const resetForm = () => {
    setFormData(initialForm);
    formatSelectedCategory();
    formatSelectedBaseMeal();
    formatMetadaList();
  };

  useEffect(() => {
    if (isEditMeal(pathname)) formatSelectedCategory();
  }, [categoryList, pathname]);
  useEffect(() => {
    if (isEditMeal(pathname) && Object.keys(meal).length > 0) {
      formatFormData();
    }
  }, [meal, brandsList]);
  useEffect(() => {
    if (isEditMeal(pathname) && meal) formatSelectedBaseMeal();
  }, [baseMealList, pathname, meal]);

  return (
    <MealContext.Provider
      value={{
        formData,
        setFormData,
        loading,
        setLoading,
        meal,
        brandsList,
        setBrandsList,
        categoryList,
        setCategoryList,
        baseMealList,
        brandLoading,
        setBrandLoading,
        selectedBrand,
        setSelectedBrand,
        resetForm,
        selectedKeywords,
        selectedBusinessMetadata,
        selectedDescriptiveMetadata,
        setSelectedBusinessMetadata,
        setSelectedDescriptiveMetadata,
        setSelectedKeywords,
        newCategory,
        setNewCategory,
        selectedCategoryList,
        setSelectedCategoryList,
        mealImage,
        setMealImage,
        disabled,
        setDisabled,
        uploading,
        setUploading,
        selectedBaseMeal,
        setSelectedBaseMeal,
        setAllBrands,
        allBrands
      }}>
      {children}
    </MealContext.Provider>
  );
};

export default MealProvider;
